import { Simplify }		from "ts-base/type";
import { Fn }			from "ts-base/fn";
import { Tuple2 }		from "ts-base/tuple";
import { Maybe }		from "ts-base/maybe";

export namespace Objects {
	//-----------------------------------------------------------------------------
	//## type helpers

	/** an identity function, but takes mutability away */
	export const readonly	= <T extends object>(self:T):Readonly<T>	=> self;

	//------------------------------------------------------------------------------

	/** this is dangerous because TS cannot ensure the Record does not include any extra keys */
	export const unsafeTypedKeys		= <S extends PropertyKey, T>(self:Record<S, T>):Array<S>	=>
		Object.keys(self) as Array<S>;

	/** this is dangerous because TS cannot ensure the Record does not include any extra keys */
	export const unsafeTypedEntries	= <S extends PropertyKey, T>(self:Record<S, T>):Array<Tuple2<S, T>>	=>
		Object.entries(self) as unknown as Array<Tuple2<S, T>>;

	/** this is dangerous because TS cannot ensure the Tuples contain all possible keys */
	export const unsafeTypedFromEntries	= <K extends PropertyKey, V>(self:ReadonlyArray<Tuple2<K, V>>):Record<K, V>	=>
		Object.fromEntries(self) as Record<K, V>;

	//------------------------------------------------------------------------------

	export const extend	= <B extends {}, E extends {}>(base:B, extension:Fn<B, E>):B & E	=> ({
		...base,
		...extension(base),
	});

	export const extendC	= <B extends {}>(base:B) => <E extends {}>(extension:Fn<B, E>):B & E	=> ({
		...base,
		...extension(base),
	});

	//------------------------------------------------------------------------------

	export const field	= <T, K extends keyof T>(self:T, field:K):T[K]	=>
		self[field];

	export const fieldNamed	= <K extends PropertyKey>(field:K) => <X>(self:Record<K, X>):X	=>
		self[field];

	export const maybeField	= (o:object, key:string):Maybe<unknown>	=>
		hasOwn(o, key) ? Maybe.some(o[key]) : Maybe.none();

	/** same as the upcoming Object.hasOwn in ES2022 */
	export const hasOwn	= <K extends string>(it:object, prop:K):it is Record<K, unknown>	=>
		Object.prototype.hasOwnProperty.call(it, prop);

	type HasField<O, K extends string>	=
		Simplify<
			O & (
				K extends keyof O
				// make known keys required
				?	Required<Pick<O, K>>
				// add unknown keys as unknown
				:	Record<K, unknown>
			)
		>;

	/** quite similar to hasOwn (and Object.hasOwn in ES2022), but with vastly improved type inference */
	export const hasField	= <O extends object, K extends string>(it:O, prop:K):it is HasField<O, K>	=>
		Object.prototype.hasOwnProperty.call(it, prop);

	/** create an object with a single field */
	export const single	= <K extends string>(k:K) => <V>(v:V):Record<K, V>	=>
		({ [k]: v }) as Record<K, V>;

	// TODO probably does not work with optional properties
	export type SetField<S, K extends PropertyKey, V>	=
		Simplify<
			// eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
			{ [P in keyof S]: P extends K ? V : S[P] } & { [P in K]: V }
		>;

	export type DistributiveSetField<S, K extends PropertyKey, V>	=
		S extends unknown ? SetField<S, K, V> : never;

	/** overwrite a field or add it */
	export const set	= <K extends PropertyKey>(key:K)	=> <S extends object, V>(base:S, value:V):DistributiveSetField<S, K, V>	=>
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		({ ...base, [key]: value }) as any;

	/** make a state transformation that transforms a single value and its type inside an object at the given key */
	export const modifyAtKey	= <S extends object>(base:S) => <K extends keyof S & PropertyKey>(key:K) => <V>(mod:Fn<S[K], V>):DistributiveSetField<S, K, V> =>
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		({ ...base, [key]: mod(base[key]) }) as any;

	//------------------------------------------------------------------------------

	/** merge objects of same type into a new one (array params) */
	export const merge = <T>(objects:ReadonlyArray<T>):T =>
		Object.assign({}, ...objects);

	/** merge objects of same type into a new one (spread params) */
	export const mergeN = <T>(...objects:ReadonlyArray<T>):T =>
		merge(objects);
}
